import React from "react";
import {
  ArrowRight,
  CalendarRange,
  CircleCheck,
  Eye,
  LayoutGrid,
  LogOut,
  Shirt,
  UserCog,
  type LucideIcon,
} from "lucide-react";
import type { AppDestination, AppDestinationId } from "../appDestinations";

// Each destination is one stable product area; workflow choices live inside it.
const DESTINATION_ICONS: Record<AppDestinationId, LucideIcon> = {
  distribution: CalendarRange,
  operations: CircleCheck,
  audit: Eye,
  administration: UserCog,
  laundry: Shirt,
};

export function AppSwitcherButton({ onOpen }: { onOpen?: () => void }) {
  if (!onOpen) return null;
  return (
    <span className="group relative inline-flex shrink-0">
      <button
        type="button"
        onClick={onOpen}
        aria-label="Aplicativos"
        className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-2 rounded-control px-2 text-foreground-muted transition-colors hover:bg-surface-subtle hover:text-foreground focus-visible:outline-none md:px-3"
      >
        <LayoutGrid aria-hidden="true" size={20} />
        <span aria-hidden="true" className="hidden text-sm font-semibold md:inline">
          Aplicativos
        </span>
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute right-0 top-full z-50 mt-2 whitespace-nowrap rounded-control bg-foreground px-2.5 py-1.5 text-xs font-medium text-surface opacity-0 shadow-subtle transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      >
        Aplicativos
      </span>
    </span>
  );
}

function DestinationCard({ destination, onSelect }: {
  destination: AppDestination;
  onSelect: (path: string) => void;
}) {
  const Icon = DESTINATION_ICONS[destination.id];

  return (
    <button
      type="button"
      onClick={() => onSelect(destination.path)}
      className="group flex h-full min-h-40 flex-col rounded-card border border-border bg-surface p-5 text-left shadow-subtle transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-secondary hover:shadow-card focus-visible:border-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/30 motion-reduce:transform-none sm:min-h-48 sm:p-6"
    >
      <span
        aria-hidden="true"
        data-testid={`destination-icon-${destination.id}`}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-control bg-brand-secondary/15 text-brand-secondary transition-colors duration-200 group-hover:bg-brand-secondary/25 group-focus-visible:bg-brand-secondary/25"
      >
        <Icon
          data-testid={`destination-glyph-${destination.id}`}
          size={21}
          strokeWidth={2}
        />
      </span>
      <h3 className="mt-4 text-lg font-semibold leading-tight text-foreground sm:mt-5">
        {destination.label}
      </h3>
      <p className="mt-2 text-sm leading-6 text-foreground-muted">
        {destination.description}
      </p>
      <span className="mt-auto inline-flex justify-end pt-4 text-foreground-muted transition-colors duration-200 group-hover:text-brand-secondary group-focus-visible:text-brand-secondary sm:pt-5">
        <ArrowRight
          aria-hidden="true"
          size={19}
          className="transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transform-none"
        />
      </span>
    </button>
  );
}

function DestinationGrid({ destinations, onSelect, ariaLabel }: {
  destinations: AppDestination[];
  onSelect: (path: string) => void;
  ariaLabel: string;
}) {
  return (
    <div
      aria-label={ariaLabel}
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      {destinations.map((destination) => (
        <DestinationCard key={destination.id} destination={destination} onSelect={onSelect} />
      ))}
    </div>
  );
}

export function AppSwitcher({ destinations, userName, onSelect, onLogout }: {
  destinations: AppDestination[];
  userName?: string;
  onSelect: (path: string) => void;
  onLogout: () => void;
}) {
  const diariaBrasilDestinations = destinations.filter(
    (destination) => destination.brand === "diaria-brasil",
  );
  const facilitaDestinations = destinations.filter(
    (destination) => destination.brand === "facilita",
  );
  const hasDiariaBrasil = diariaBrasilDestinations.length > 0;

  return (
    <main
      data-brand={hasDiariaBrasil ? "diaria-brasil" : "facilita"}
      className="min-h-screen bg-surface-canvas"
    >
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex min-h-16 max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <img
              src={
                hasDiariaBrasil
                  ? "/logo-diaria-brasil.png"
                  : "/logo-facilita-lavanderia.png"
              }
              alt={hasDiariaBrasil ? "Diária Brasil" : "Facilita Lavanderia"}
              className="h-10 w-28 shrink-0 object-contain object-left sm:h-11 sm:w-32"
            />
            {hasDiariaBrasil && (
              <span
                data-testid="portal-identity"
                className="flex items-center gap-3 max-[360px]:hidden"
              >
                <span
                  data-testid="portal-divider"
                  aria-hidden="true"
                  className="h-7 w-px bg-border-strong"
                />
                <span className="whitespace-nowrap text-xs font-medium text-foreground-muted sm:text-sm">
                  Portal Operacional
                </span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {userName && <span className="hidden max-w-48 truncate text-sm text-foreground-muted sm:block">{userName}</span>}
            <button
              type="button"
              onClick={onLogout}
              aria-label="Sair"
              title="Sair"
              className="inline-flex min-h-11 items-center gap-2 rounded-control px-3 text-sm font-semibold text-foreground-muted transition-colors hover:bg-surface-subtle hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/30 max-[360px]:min-w-11 max-[360px]:justify-center max-[360px]:px-0"
            >
              <LogOut aria-hidden="true" size={17} />
              <span className="max-[360px]:hidden">Sair</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Meus aplicativos</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-foreground-muted sm:text-base">
            Acesse os sistemas disponíveis de acordo com as permissões do seu perfil.
          </p>
        </div>

        {hasDiariaBrasil && <section aria-labelledby="diaria-brasil-apps" className="mt-10">
          <div className="mb-6 flex items-stretch gap-4">
            <span
              aria-hidden="true"
              className="w-1 shrink-0 rounded-full bg-brand-secondary"
            />
            <div>
              <h2
                id="diaria-brasil-apps"
                className="text-xl font-semibold text-foreground"
              >
                Diária Brasil
              </h2>
              <p className="mt-1.5 text-sm leading-6 text-foreground-muted">
                Operação, gestão e auditoria da rede hoteleira.
              </p>
            </div>
          </div>
          <DestinationGrid
            destinations={diariaBrasilDestinations}
            onSelect={onSelect}
            ariaLabel="Aplicativos Diária Brasil"
          />
        </section>}

        {hasDiariaBrasil && facilitaDestinations.length > 0 && (
          <div aria-hidden="true" className="my-10 border-t border-border sm:my-12" />
        )}

        {facilitaDestinations.length > 0 && <section data-brand="facilita" aria-labelledby="facilita-apps" className={hasDiariaBrasil ? undefined : "mt-10"}>
          <div className="mb-6 flex items-stretch gap-4">
            <span
              aria-hidden="true"
              className="w-1 shrink-0 rounded-full bg-brand"
            />
            <div>
              <h2
                id="facilita-apps"
                className="text-xl font-semibold text-foreground"
              >
                Facilita Lavanderia
              </h2>
              <p className="mt-1.5 text-sm leading-6 text-foreground-muted">
                Soluções especializadas para a operação de lavanderia.
              </p>
            </div>
          </div>
          <DestinationGrid
            destinations={facilitaDestinations}
            onSelect={onSelect}
            ariaLabel="Aplicativos Facilita"
          />
        </section>}
      </div>
    </main>
  );
}
